import { useEffect, useCallback } from 'react';
import QueryString from 'query-string';
import {
  useDeviceManager,
  useDeviceManagerState,
} from 'app/containers/rtc/DeviceManagerContext';
import { useHistory } from 'app/components/router/History';
import { useRtcState } from 'app/containers/rtc/RtcStateContext';
import ConnectionState from 'app/services/rtc/ConnectionState';
import { isScreenSharingSourceActive } from 'app/models/rtc/selectors';
import { ScreenSharingContentViewRoute, FullScreenCallRoute } from 'app/constants/Routes';
import { useActiveRoute } from 'app/components/router/ActiveRoute';
import {
  IScreenSharingContentViewRouteParams,
} from 'app/components/rtc/ScreenSharingContentView/ScreenSharingContentViewPage';
import IRoute from 'app/constants/Routes/IRoute';
import { usePeers } from 'app/containers/rtc/PeersContext';
import usePrevious from '@engoo/react-hooks/usePrevious';
import {
  RETURN_FROM_SCREEN_SHARING_TO_QUERY_PARAM,
  useReturnFromScreenSharing,
} from 'app/components/rtc/ReturnFromScreenSharingParamContext';

export const isScreenSharingRoute = (
  route: IRoute | undefined, params?: unknown,
): params is IScreenSharingContentViewRouteParams => {
  return route === ScreenSharingContentViewRoute;
};

export const useIsOnScreenSharingRoute = () => {
  const { route } = useActiveRoute();

  if (!route) return false;

  return isScreenSharingRoute(route);
};

export const useIsScreenSharingSourceActive = () => {
  const devices = useDeviceManagerState();
  return isScreenSharingSourceActive(devices.screen);
};

export const useIsParticipantInScreenContentMode = (
  participantId: string,
) => {
  const { route, params } = useActiveRoute();

  if (!isScreenSharingRoute(route, params)) {
    return false;
  }

  return params.participantId === participantId;
};

export const useIsCallerPresentingScreen = (participantId: string) => {
  const { caller } = useRtcState();

  return participantId === caller?.id;
};

export const useScreenSharingContentModeAutoDisable = (participantId: string) => {
  const deviceManager = useDeviceManager();
  const isScreenFeedActive = useIsScreenSharingSourceActive();
  const isCallerPresenting = useIsCallerPresentingScreen(participantId);

  // Disable on unmount
  useEffect(() => {
    if (isScreenFeedActive) {
      return () => {
        deviceManager.stopScreenSharing();
      };
    }
  }, [isScreenFeedActive, deviceManager]);

  // Disable when we are no longer presenting
  useEffect(() => {
    if (!isCallerPresenting) {
      deviceManager.stopScreenSharing();
    }
  }, [isCallerPresenting, deviceManager]);
};

const useBackNavigationUrl = () => {
  const isOnScreenSharingRoute = useIsOnScreenSharingRoute();
  const [existingReturnPath] = useReturnFromScreenSharing();
  const history = useHistory();

  const currentLocation: string = history.extendHref({});

  if (!isOnScreenSharingRoute) {
    return currentLocation;
  }

  return existingReturnPath;
};

export const useRedirectToScreenSharingContentModeOnStreamActivation = () => {
  const history = useHistory();
  const { caller } = useRtcState();
  const isScreenFeedActive = useIsScreenSharingSourceActive();
  const wasScreenFeedActive = usePrevious(isScreenFeedActive);
  const backNavigationUrl = useBackNavigationUrl();

  useEffect(() => {
    if (
      !caller ||
      !isScreenFeedActive
    ) return;

    // active route changes on every navigation
    // causing this effect to re-run
    if (wasScreenFeedActive === isScreenFeedActive) return;

    history.push({
      search: QueryString.stringify({
        [RETURN_FROM_SCREEN_SHARING_TO_QUERY_PARAM]: backNavigationUrl,
      }),
      pathname: ScreenSharingContentViewRoute.getPathname({
        participantId: caller.id,
      }),
    });
  }, [
    caller, isScreenFeedActive, wasScreenFeedActive,
    history, backNavigationUrl,
  ]);
};

const useReturnLocation = () => {
  const [returnLocation] = useReturnFromScreenSharing();

  if (returnLocation) {
    return returnLocation;
  }

  return {
    pathname: FullScreenCallRoute.getPathname(),
  };
};

export const useLeaveContentModeOnScreenStreamEnd = (participantId: string) => {
  const history = useHistory();
  const { signalingState } = useRtcState();
  const returnLocation = useReturnLocation();

  const leaveContentMode = useCallback(() => {
    history.push(returnLocation);
  }, [history, returnLocation]);

  const isCallerPresenting = useIsCallerPresentingScreen(participantId);
  const isScreenFeedActive = useIsScreenSharingSourceActive();

  useEffect(() => {
    // Leaving the meeting causes screen sharing to be disabled
    // Don't want to redirect anywhere in this case
    if (signalingState !== ConnectionState.On) return;

    // Only leave when screen sharing was disabled
    // And effect is running for the presenter
    if (!isCallerPresenting || isScreenFeedActive) return;

    leaveContentMode();
  }, [
    history,
    isScreenFeedActive,
    isCallerPresenting,
    signalingState,
    leaveContentMode,
  ]);
};

export const useScreenPresenterPeer = (participantId: string) => {
  return usePeers().get(participantId);
};

export const useScreenPresenterStream = (participantId: string) => {
  const isCallerPresenting = useIsCallerPresentingScreen(participantId);
  const presenter = useScreenPresenterPeer(participantId);
  const { screen } = useDeviceManagerState();

  if (isCallerPresenting) {
    return screen.stream;
  }

  return presenter?.screenStream;
};
